import { TICK_YEARS, xAxisBaseProps, Y_AXIS_LABEL_TOP_OFFSET, yAxisBaseProps, yAxisLabelProps } from "@/components/charts/axis";
import { CustomReferenceLabel } from "@/components/charts/CustomReferenceLabel";
import { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { ADAPTATION_TERM_COLORS, AdaptationType } from "@/types/adaptation";
import { Rcp } from "@/types/rcp";
import { formatCurrency, formatPercent } from "@/utils/number";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import { useCollectionState } from "../../context/CollectionContext";
import { BreakEvenInvestmentSeriesRow, buildBreakEvenInvestmentSeries } from "../utils/adaptationRoi";
import { formatYAxisTick, getMetricShortLabel } from "./common/utils";
type BreakEvenInvestmentChartProps = {
  baselineSummary: PeaSummaryResponse;
  comparisonSummary: PeaSummaryResponse;
  comparisonType: AdaptationType;
  rcp: Rcp;
  hazard: HazardPlusTotal;
  selectedYear: Year;
  discountRate: number;
  baseYear: number;
  isPointLevel?: boolean;
};
const GRID_COLOR = "currentColor";
const ChartTooltip = ({
  active,
  payload,
  baseYear,
  discountRate
}: TooltipProps<number, string> & {
  baseYear: number;
  discountRate: number;
}) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.adaptationInsights.charts.cumilativeRisk"
  });
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as BreakEvenInvestmentSeriesRow;
  const rows = [{
    label: t("tooltip.discountRate"),
    value: formatPercent(discountRate / 100, 2)
  }, {
    label: t("tooltip.cumulativeBreakevenInvestment"),
    value: formatCurrency(row.cumulativeBreakevenInvestment, false)
  }];
  return <div className="rounded-lg border border-gray-200 bg-surface/80 p-3 text-gray-700 shadow-lg backdrop-blur backdrop-saturate-200" data-sentry-component="ChartTooltip" data-sentry-source-file="BreakEvenInvestmentChart.tsx">
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-1">
        <span className="mb-1 font-semibold">{t("tooltip.cumulativePeriod")}</span>
        <span className="mb-1 text-right font-semibold tabular-nums">
          {baseYear} - {row.year}
        </span>
        {rows.map(({
        label,
        value
      }) => <div key={label} className="contents">
            <span className="text-sm">{label}</span>
            <span className="text-right tabular-nums">{value}</span>
          </div>)}
      </div>
    </div>;
};
export const BreakEvenInvestmentChart = ({
  baselineSummary,
  comparisonSummary,
  comparisonType,
  rcp,
  hazard,
  selectedYear,
  discountRate,
  baseYear,
  isPointLevel = false
}: BreakEvenInvestmentChartProps) => {
  const metricLabel = getMetricShortLabel("ttip", isPointLevel);
  const lineColor = ADAPTATION_TERM_COLORS[comparisonType];
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.hazardBreakdown"
  });
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.adaptationInsights.charts.cumilativeRisk"
  });
  const {
    setYear
  } = useCollectionState();
  const chartData = useMemo(() => buildBreakEvenInvestmentSeries({
    baselineSummary,
    comparisonSummary,
    rcp,
    hazard,
    discountRate,
    baseYear
  }), [baselineSummary, comparisonSummary, rcp, hazard, discountRate, baseYear]);
  const tickYears = useMemo(() => TICK_YEARS.filter(year => year >= baseYear), [baseYear]);
  return <div className="h-[300px] w-full rounded-xl border border-subtle px-2 py-4" data-sentry-component="BreakEvenInvestmentChart" data-sentry-source-file="BreakEvenInvestmentChart.tsx">
      <ResponsiveContainer data-sentry-element="ResponsiveContainer" data-sentry-source-file="BreakEvenInvestmentChart.tsx">
        {!chartData.length ? <div className="flex h-full items-center justify-center text-sm text-gray-400">
            {tHazardBreakdown("unavailableMessage")}
          </div> : <ComposedChart data={chartData}
      /* top: headroom for the axis label, which sits above the inset tick values */ margin={{
        top: Y_AXIS_LABEL_TOP_OFFSET,
        right: 0,
        left: 0,
        bottom: 0
      }} onClick={state => {
        if (typeof state?.activeLabel === "number") setYear(state.activeLabel as Year);
      }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} strokeOpacity={0.15} />
            <XAxis {...xAxisBaseProps} dataKey="year" type="number" domain={["dataMin", "dataMax"]} ticks={tickYears} interval="preserveStartEnd" />
            <YAxis {...yAxisBaseProps} tickFormatter={val => formatYAxisTick("ttip", val)} label={yAxisLabelProps(metricLabel)} />
            <Tooltip content={<ChartTooltip baseYear={baseYear} discountRate={discountRate} />} />

            <Line type="monotoneX" dataKey="cumulativeBreakevenInvestment" name={t("tooltip.cumulativeBreakevenInvestment") ?? ""} stroke={lineColor} strokeWidth={3} dot={false} isAnimationActive={false} />

            <ReferenceLine x={selectedYear} strokeWidth={2} strokeDasharray="4 4" label={<CustomReferenceLabel value={selectedYear} />} />
          </ComposedChart>}
      </ResponsiveContainer>
    </div>;
};
export default BreakEvenInvestmentChart;